import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Heart, CreditCard, Lock } from 'lucide-react';
const PRESET_AMOUNTS = [5, 10, 25, 50];
export function DonationForm() {
  const [amount, setAmount] = useState<number | 'custom'>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalAmount = amount === 'custom' ? parseFloat(customAmount) : amount;
    if (!finalAmount || finalAmount <= 0) {
      toast.error('Por favor, introduce una cantidad válida.');
      return;
    }
    setIsSubmitting(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      toast.success('Donación procesada con éxito');
    }, 2000);
  };
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-white p-8 md:p-12 rounded-2xl shadow-soft text-center border border-cafe-border/50" data-id="element-20">
        <div className="w-20 h-20 bg-cafe-card rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-21">
          <Heart className="text-cafe-accent w-10 h-10 fill-cafe-accent" data-id="element-22" />
        </div>
        <h3 className="text-3xl font-serif text-cafe-primary mb-4" data-id="element-23">
          ¡Gracias por tu generosidad!
        </h3>
        <p className="text-cafe-text/80 mb-8 text-lg max-w-md mx-auto" data-id="element-24">
          Tu donación de {amount === 'custom' ? customAmount : amount}€ ha sido
          procesada. El 100% de esta cantidad irá destinada a la Fundación
          Española de Enfermedades Priónicas.
        </p>
        <button onClick={() => {
        setIsSuccess(false);
        setAmount(10);
        setCustomAmount('');
      }} className="text-cafe-accent hover:text-cafe-primary font-medium transition-colors underline underline-offset-4" data-id="element-25">
          Hacer otra donación
        </button>
      </motion.div>;
  }
  return <motion.form initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} onSubmit={handleSubmit} className="bg-white p-6 md:p-10 rounded-2xl shadow-soft border border-cafe-border/50" data-id="element-26">
      {/* Amount Selection */}
      <div className="mb-8" data-id="element-27">
        <label className="block text-sm font-medium text-cafe-text mb-4" data-id="element-28">
          Selecciona una cantidad
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-id="element-29">
          {PRESET_AMOUNTS.map(preset => <button key={preset} type="button" onClick={() => setAmount(preset)} className={`py-3 rounded-xl font-medium transition-colors border ${amount === preset ? 'bg-cafe-primary text-white border-cafe-primary' : 'bg-cafe-bg text-cafe-text border-cafe-border hover:border-cafe-accent'}`} data-id="element-30">
              {preset}€
            </button>)}
          <button type="button" onClick={() => setAmount('custom')} className={`py-3 rounded-xl font-medium transition-colors border ${amount === 'custom' ? 'bg-cafe-primary text-white border-cafe-primary' : 'bg-cafe-bg text-cafe-text border-cafe-border hover:border-cafe-accent'}`} data-id="element-31">
            Otra
          </button>
        </div>
        {amount === 'custom' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="mt-4" data-id="element-32">
            <div className="relative" data-id="element-33">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-cafe-text/50" data-id="element-34">
                €
              </span>
              <input type="number" min="1" step="1" required value={customAmount} onChange={e => setCustomAmount(e.target.value)} placeholder="Introduce cantidad" className="w-full pl-8 pr-4 py-3 rounded-lg bg-cafe-bg border border-cafe-border focus:border-cafe-accent focus:ring-1 focus:ring-cafe-accent outline-none transition-colors" data-id="element-35" />
            </div>
          </motion.div>}
      </div>
      {/* Personal Info */}
      <div className="space-y-4 mb-8" data-id="element-36">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-37">
          <div className="space-y-2" data-id="element-38">
            <label htmlFor="donante-nombre" className="block text-sm font-medium text-cafe-text" data-id="element-39">
              Nombre
            </label>
            <input type="text" id="donante-nombre" required className="w-full px-4 py-3 rounded-lg bg-cafe-bg border border-cafe-border focus:border-cafe-accent outline-none transition-colors" data-id="element-40" />
          </div>
          <div className="space-y-2" data-id="element-41">
            <label htmlFor="donante-email" className="block text-sm font-medium text-cafe-text" data-id="element-42">
              Email
            </label>
            <input type="email" id="donante-email" required className="w-full px-4 py-3 rounded-lg bg-cafe-bg border border-cafe-border focus:border-cafe-accent outline-none transition-colors" data-id="element-43" />
          </div>
        </div>
        <div className="space-y-2" data-id="element-44">
          <label htmlFor="donante-mensaje" className="block text-sm font-medium text-cafe-text" data-id="element-45">
            Mensaje de apoyo (Opcional)
          </label>
          <textarea id="donante-mensaje" rows={2} className="w-full px-4 py-3 rounded-lg bg-cafe-bg border border-cafe-border focus:border-cafe-accent outline-none transition-colors resize-none" data-id="element-46" />
        </div>
      </div>
      {/* Payment Placeholder */}
      <div className="bg-cafe-bg rounded-xl p-6 mb-8 border border-cafe-border" data-id="element-47">
        <div className="flex items-center justify-between mb-4" data-id="element-48">
          <h4 className="font-medium text-cafe-text flex items-center" data-id="element-49">
            <CreditCard className="w-5 h-5 mr-2 text-cafe-text/60" data-id="element-50" />
            Datos de pago
          </h4>
          <Lock className="w-4 h-4 text-cafe-text/40" data-id="element-51" />
        </div>
        <div className="space-y-4 opacity-70" data-id="element-52">
          <input type="text" placeholder="Número de tarjeta" disabled className="w-full px-4 py-3 rounded-lg bg-white border border-cafe-border cursor-not-allowed" data-id="element-53" />
          <div className="grid grid-cols-2 gap-4" data-id="element-54">
            <input type="text" placeholder="MM/AA" disabled className="w-full px-4 py-3 rounded-lg bg-white border border-cafe-border cursor-not-allowed" data-id="element-55" />
            <input type="text" placeholder="CVC" disabled className="w-full px-4 py-3 rounded-lg bg-white border border-cafe-border cursor-not-allowed" data-id="element-56" />
          </div>
          <p className="text-xs text-cafe-text/60 text-center mt-2" data-id="element-57">
            * Esto es una simulación. No se realizarán cargos reales.
          </p>
        </div>
      </div>
      <button type="submit" disabled={isSubmitting} className="w-full bg-cafe-accent text-white py-4 rounded-xl font-medium text-lg hover:bg-cafe-accentHover transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center shadow-sm" data-id="element-58">
        {isSubmitting ? <span className="flex items-center" data-id="element-59">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-60">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-61"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-62"></path>
            </svg>
            Procesando donación...
          </span> : `Donar ${amount === 'custom' ? customAmount || '0' : amount}€`}
      </button>
    </motion.form>;
}